import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Building2, Loader2, Plus, Search, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Separator } from "@/frontend/components/ui/separator";
import { orpc } from "@/frontend/orpc-client";
import { logoUrl } from "@/frontend/utils/shared";
import { serializeSources, toLocalSources } from "@/frontend/utils/dashboard/settings/backend";
import { getProviderDocsUrl } from "@/frontend/utils/dashboard/settings/ehr-docs";
import { useBackendQuery } from "@/frontend/queries/dashboard/settings";
import { useSaveBackendMutation, useDisableBackendMutation } from "@/frontend/mutations/dashboard/settings";
import { DocsHelpLink } from "./DocsHelpLink";
import { ProviderLogo } from "./ProviderLogo";
import { CustomCredentialsCard } from "./CustomCredentialsCard";
import { CREDENTIAL_MODE } from "@/frontend/types/ehr-clients";
import type { CredentialMode, Provider, BackendData, LocalGroup, LocalSource } from "@/frontend/types/ehr-clients";

interface Props {
  provider: Provider;
}

/**
 * Detail view for the Epic Backend (SMART Backend Services / bulk data) provider.
 * Fetches the org's backend config, then renders the editable view. Environment
 * is inferred from the org.
 */
export function EhrBackendDetailView({ provider }: Props) {
  const { data, isLoading } = useBackendQuery(provider.type);
  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <BackendConfigView provider={provider} data={data} />;
}

/**
 * Editable backend config. All edits (client ID, sources, Group IDs) are staged
 * in local state; the Save button persists the whole config in one shot via
 * `saveBackend`. Disable is an immediate action.
 */
function BackendConfigView({ provider, data }: { provider: Provider; data: BackendData }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Backend services: no redirect URI (no auth-code flow), but Epic must have the
  // public key to verify the client_credentials assertion — so JWKS is shown.
  const jwksUri = `${origin}/api/auth/.well-known/jwks`;
  const sourceType = provider.type.replace(/-backend$/, "");
  const docsUrl = getProviderDocsUrl(provider.type);

  const savedCredentialMode: CredentialMode = data.client_id ? CREDENTIAL_MODE.OWN : CREDENTIAL_MODE.MEDBLOCKS;
  const [credentialMode, setCredentialMode] = useState<CredentialMode>(savedCredentialMode);
  const [clientId, setClientId] = useState(data.client_id ?? "");
  const [sources, setSources] = useState<LocalSource[]>(() => toLocalSources(data));
  const [orgQuery, setOrgQuery] = useState("");

  const save = useSaveBackendMutation();
  const disableBackend = useDisableBackendMutation();

  // Search fhir_sources scoped to the org's environment (server-side) + the Epic type.
  const query = orgQuery.trim();
  const { data: results = [] } = useQuery({
    ...orpc.fhirSources.search.queryOptions({ input: { query, limit: 10, type: sourceType } }),
    enabled: query.length >= 2,
  });
  const addedUrls = new Set(sources.map((s) => s.fhir_source));
  const searchResults = results.filter((s) => !addedUrls.has(s.fhirBaseUrl));

  const enabled = !data.disabled;
  const connectionStatusText = !enabled
    ? "Not connected"
    : savedCredentialMode === CREDENTIAL_MODE.OWN
    ? "Using custom credentials"
    : "Using Medblocks' credentials";

  const isClientIdRequired = credentialMode === CREDENTIAL_MODE.OWN && !clientId.trim();
  const credsChanged =
    credentialMode !== savedCredentialMode ||
    (credentialMode === CREDENTIAL_MODE.OWN && clientId.trim() !== (data.client_id ?? ""));
  const sourcesChanged = serializeSources(sources) !== serializeSources(toLocalSources(data));
  const hasUnsavedChanges = credsChanged || sourcesChanged;
  const canSave = hasUnsavedChanges && !isClientIdRequired;
  const isMutationPending = save.isPending || disableBackend.isPending;

  function buildSavePayload() {
    const value = credentialMode === CREDENTIAL_MODE.OWN ? clientId.trim() || null : null;
    return {
      providerType: provider.type,
      clientId: value,
      sources: sources.map((s) => ({ fhirBaseUrl: s.fhir_source, groups: s.groups.map((g) => ({ groupId: g.group_id, label: g.label })) })),
    };
  }

  function handleSave() {
    if (!canSave) return;
    save.mutate(buildSavePayload());
  }

  function handleEnable() {
    // Re-save the current state (clears disabled_at).
    save.mutate(buildSavePayload());
  }

  function handleDisable() {
    disableBackend.mutate({ providerType: provider.type });
  }

  // ── Local source/group editing (staged until Save) ──
  function addSource(source: { fhirBaseUrl: string; name: string; logo: string | null }) {
    setSources((prev) => [...prev, { fhir_source: source.fhirBaseUrl, name: source.name, logo: source.logo, groups: [] }]);
    setOrgQuery("");
  }
  function removeSource(fhirSource: string) {
    setSources((prev) => prev.filter((s) => s.fhir_source !== fhirSource));
  }
  function setSourceGroups(fhirSource: string, groups: LocalGroup[]) {
    setSources((prev) => prev.map((s) => (s.fhir_source === fhirSource ? { ...s, groups } : s)));
  }

  return (
    <div className="flex flex-col gap-8">
      <Link
        to="/dashboard/settings"
        search={{ section: "clients", provider: "", destinationId: undefined }}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to Connections
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex size-10 items-center justify-center rounded-lg border bg-white p-1.5 shrink-0">
            <ProviderLogo provider={provider.type} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{provider.label} Backend</h2>
              <DocsHelpLink
                href={docsUrl}
                tooltip={provider.description}
                ariaLabel={`Open ${provider.label} Backend integration guide`}
              />
              {enabled ? (
                <span className="inline-flex items-center rounded-md bg-green-50 border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700">
                  Enabled
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md bg-red-50 border border-red-200 px-2 py-0.5 text-xs font-medium text-red-700">
                  Disabled
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{connectionStatusText}</p>
          </div>
        </div>
        {hasUnsavedChanges ? (
          <Button size="sm" onClick={handleSave} disabled={isMutationPending || !canSave}>
            {save.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
            Save
          </Button>
        ) : !enabled ? (
          <Button size="sm" onClick={handleEnable} disabled={isMutationPending}>
            {save.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
            Enable connection
          </Button>
        ) : null}
      </div>

      <Separator />

      {/* About */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3">About</p>
        <p className="text-sm text-muted-foreground">{provider.description}</p>
      </div>

      {/* Client ID (read-only, to register in the EHR) */}
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">Client ID</p>
        <p className="text-xs text-muted-foreground">Share this with the organization to add to their Epic installation.</p>
        {data.client_id_in_use ? (
          <code className="mt-1 inline-flex w-fit items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all">
            {data.client_id_in_use}
          </code>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground italic">No client ID configured yet.</p>
        )}
      </div>

      {/* Credentials — Client ID only (override optional), only when enabled */}
      {enabled && (
        <CustomCredentialsCard
          enabled={credentialMode === CREDENTIAL_MODE.OWN}
          onToggle={() =>
            setCredentialMode(credentialMode === CREDENTIAL_MODE.OWN ? CREDENTIAL_MODE.MEDBLOCKS : CREDENTIAL_MODE.OWN)
          }
          clientId={clientId}
          onClientIdChange={setClientId}
          registrationUrl={provider.registrationUrl}
          providerLabel={provider.label}
          docsUrl={docsUrl}
          jwksUri={jwksUri}
          showRedirectUri={false}
          error={save.isError ? save.error.message : null}
        />
      )}

      {/* Organizations + Group IDs (only when enabled) */}
      {enabled && (
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">Organizations</p>
            <p className="text-xs text-muted-foreground mt-1">
              Search and add the Epic organizations you pull from, then configure their FHIR Group IDs. Changes are saved with the Save button.
            </p>
          </div>

          {/* Search + add */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={orgQuery}
              onChange={(e) => setOrgQuery(e.target.value)}
              placeholder="Search Epic organizations"
              className="pl-9 text-sm"
            />
            {searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border bg-popover shadow-md">
                {searchResults.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => addSource(source)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <SourceLogo logo={source.logo} />
                      <span className="truncate">{source.name}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-primary shrink-0">
                      <Plus className="size-3.5" /> Add
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Added organizations */}
          {sources.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No organizations added yet.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {sources.map((source) => (
                <OrgBackendCard
                  key={source.fhir_source}
                  source={source}
                  onRemove={() => removeSource(source.fhir_source)}
                  onGroupsChange={(groups) => setSourceGroups(source.fhir_source, groups)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scopes */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3">Scopes</p>
        <div className="flex flex-wrap gap-1.5">
          {provider.scopes.map((scope) => (
            <span
              key={scope}
              className="inline-flex items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all"
            >
              {scope}
            </span>
          ))}
        </div>
      </div>

      {/* Danger zone (only when enabled) */}
      {enabled && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-destructive mb-3">Danger zone</div>
          <div className="border border-destructive/30 rounded-lg p-3">
            <p className="text-sm font-medium mb-1">Disable connection</p>
            <p className="text-xs text-muted-foreground mb-3">
              {provider.label} backend access will be turned off. You can re-enable at any time.
            </p>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs gap-1.5"
              onClick={handleDisable}
              disabled={isMutationPending}
            >
              {disableBackend.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Disable
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Card for a single staged Epic organization: a table of its Group IDs (with optional labels) plus an add row. */
function OrgBackendCard({
  source,
  onRemove,
  onGroupsChange,
}: {
  source: LocalSource;
  onRemove: () => void;
  onGroupsChange: (groups: LocalGroup[]) => void;
}) {
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupLabel, setNewGroupLabel] = useState("");

  function addGroupId() {
    const value = newGroupId.trim();
    if (!value || source.groups.some((g) => g.group_id === value)) {
      setNewGroupId("");
      setNewGroupLabel("");
      return;
    }
    onGroupsChange([...source.groups, { group_id: value, label: newGroupLabel.trim() || null }]);
    setNewGroupId("");
    setNewGroupLabel("");
  }

  return (
    <div className="rounded-xl border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <SourceLogo logo={source.logo} />
          <p className="text-sm font-medium truncate">{source.name ?? source.fhir_source}</p>
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground shrink-0" onClick={onRemove}>
          Remove
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">Group IDs</span>
        {source.groups.length > 0 && (
          <div className="rounded-md border bg-background overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                  <th className="px-3.5 py-2">Label</th>
                  <th className="px-3.5 py-2">Group ID</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {source.groups.map((g) => (
                  <tr key={g.group_id} className="border-b last:border-0">
                    <td className="px-3.5 py-2.5">
                      {g.label ? g.label : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3.5 py-2.5 font-mono text-muted-foreground">{g.group_id}</td>
                    <td className="px-3.5 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onGroupsChange(source.groups.filter((x) => x.group_id !== g.group_id))}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="size-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input
            value={newGroupLabel}
            onChange={(e) => setNewGroupLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGroupId(); } }}
            placeholder="Label (optional)"
            className="text-xs bg-background"
          />
          <Input
            value={newGroupId}
            onChange={(e) => setNewGroupId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGroupId(); } }}
            placeholder="FHIR Group ID"
            className="font-mono text-xs bg-background"
          />
          <Button size="sm" variant="outline" className="h-9 gap-1.5 shrink-0" onClick={addGroupId} disabled={!newGroupId.trim()}>
            <Plus className="size-3.5" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}

/** EHR logo thumbnail with a Building2 fallback, matching the patient hospital picker. */
function SourceLogo({ logo }: { logo: string | null }) {
  const [imgError, setImgError] = useState(false);
  const url = logo ? logoUrl(logo) : null;
  return (
    <div className="flex h-7 w-10 items-center justify-center rounded-md border bg-background px-1 overflow-hidden shrink-0">
      {url && !imgError ? (
        <img src={url} alt="" className="max-w-full max-h-full object-contain" onError={() => setImgError(true)} />
      ) : (
        <Building2 className="size-4 text-muted-foreground" />
      )}
    </div>
  );
}
